import { useMemo, useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PrintIcon from "@mui/icons-material/Print";
import EventNoteIcon from "@mui/icons-material/EventNote";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import dayjs from "dayjs";

import {
  saveActivityGroup,
  deleteActivityGroup,
  updateActivityGroupMembers,
  getActivityGroupsByDate,
  getActivityLeaders,
} from "../../api/activitiesApi";

import { useActivityCandidates } from "../../hooks/useActivityCandidates";
import { useActivityDialog } from "../../hooks/useActivityDialog";
import { useActivityReferenceData } from "../../hooks/useActivityReferenceData";

import {
  buildFilteredActivityGroups,
  buildSelectedCampusLabel,
  buildUnbookedCandidates,
  buildBookedCandidateGroups,
  buildMemberPayload,
  holdingGroupKind,
  isEventProductType,
} from "../../utils/activityBoardUtils";

import AddEditActivityDialog from "../../dialogs/AddEditActivityDialog";
import CopyGroupsDialog from "../../dialogs/CopyGroupsDialog";
import ActivityCandidatesPanel from "../panels/ActivityCandidatesPanel";
import ActivityGroupsPanel from "../panels/ActivityGroupsPanel";
import { downloadActivityRegistersPdf } from "../../utils/activityRegistersPdf";
import { downloadWeeklyRotaPdf } from "../../utils/weeklyRotaPdf";
import ActivityTransportColumn from "./ActivityTransportColumn";
import CollapsibleColumn from "./CollapsibleColumn";
import useActivityTransport from "../../hooks/useActivityTransport";
import { can } from "../../../shared utils/auth";

export default function CampusActivitiesBoard({
  campuses = [],
  campusesLoading,
  groups = [],
  loading,
  error,
  selectedDate,
  setSelectedDate,
  selectedCampusName,
  setSelectedCampusName,
  selectedSlot,
  setSelectedSlot,
  onRefresh,
  showToast,
}) {
  const campDateStr = useMemo(
    () => (selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : null),
    [selectedDate]
  );

  const isWeekend = useMemo(() => {
    if (!selectedDate) return false;
    const day = dayjs(selectedDate).day();
    return day === 0 || day === 6;
  }, [selectedDate]);

  const slotOptions = useMemo(() => {
    if (isWeekend) {
      return [
        { value: "DAY", label: "Daytime" },
        { value: "EVENING", label: "Evening" },
      ];
    }

    return [
      { value: "AFTERNOON", label: "Afternoon" },
      { value: "EVENING", label: "Evening" },
    ];
  }, [isWeekend]);

  useEffect(() => {
    const validValues = slotOptions.map((option) => option.value);
    if (!validValues.includes(selectedSlot)) {
      setSelectedSlot(slotOptions[0]?.value || "");
    }
  }, [slotOptions, selectedSlot, setSelectedSlot]);

  const campusList = useMemo(
    () => (campuses || []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [campuses]
  );

  const {
    candidateRows,
    loading: candidatesLoading,
    error: candidatesError,
    refetch: reloadCandidates,
  } = useActivityCandidates({
    date: campDateStr,
    slot: selectedSlot,
    // Only a concrete campus narrows the fetch; "All"/"UNASSIGNED" pull everything.
    campus:
      selectedCampusName && selectedCampusName !== "UNASSIGNED"
        ? selectedCampusName
        : undefined,
  });

  const {
    dialogOpen,
    editingGroup,
    openAddDialog,
    openEditDialog,
    closeDialog,
  } = useActivityDialog();

  const {
    leaders,
    products,
    loading: referenceDataLoading,
    error: referenceDataError,
    refetch: reloadReferenceData,
  } = useActivityReferenceData({
    date: campDateStr,
    campus: selectedCampusName,
  });

  const refreshBoard = useCallback(async () => {
    await onRefresh?.();
    await reloadCandidates?.();
    await reloadReferenceData?.();
  }, [onRefresh, reloadCandidates, reloadReferenceData]);

  const filteredGroups = useMemo(
    () =>
      buildFilteredActivityGroups({
        groups,
        selectedCampusName,
        selectedSlot,
      }),
    [groups, selectedCampusName, selectedSlot]
  );

  const unbookedCandidates = useMemo(
    () =>
      buildUnbookedCandidates({
        candidateRows,
        selectedCampusName,
        selectedSlot,
        products,
        // Exclude anyone already a member of a group in this campus+slot.
        activityGroups: filteredGroups,
      }),
    [candidateRows, selectedCampusName, selectedSlot, products, filteredGroups]
  );

  const bookedCandidateGroups = useMemo(
    () =>
      buildBookedCandidateGroups({
        candidateRows,
        selectedCampusName,
        products,
        // Judge "already in a group" against the groups for THIS campus + slot only.
        // Using the raw all-day/all-campus list (which can include other slots and
        // duplicate/phantom shells) wrongly drops candidates who are unassigned in
        // this slot, so they appear in neither the candidate list nor a visible group.
        activityGroups: filteredGroups,
        selectedSlot,
      }),
    [candidateRows, selectedCampusName, products, filteredGroups, selectedSlot]
  );

  const selectedCampusLabel = useMemo(
    () => buildSelectedCampusLabel({ selectedCampusName, campusList }),
    [selectedCampusName, campusList]
  );

  const [copyDialogOpen, setCopyDialogOpen] = useState(false);

  // Weekly rota PDF: pick which days of the current week to include.
  const [rotaDialogOpen, setRotaDialogOpen] = useState(false);
  const [rotaBusy, setRotaBusy] = useState(false);
  const [rotaDays, setRotaDays] = useState([]); // array of "YYYY-MM-DD"
  const MAX_ROTA_DAYS = 5;

  // Mon–Sun of the week containing the selected date.
  const weekDays = useMemo(() => {
    if (!selectedDate) return [];
    const dow = selectedDate.day(); // 0=Sun … 6=Sat
    const monday = selectedDate.subtract(dow === 0 ? 6 : dow - 1, "day");
    return Array.from({ length: 7 }, (_, i) => monday.add(i, "day"));
  }, [selectedDate]);

  const hasConcreteCampus = Boolean(
    selectedCampusName && selectedCampusName !== "UNASSIGNED"
  );

  // When opening the picker, default to the Mon–Fri weekdays (up to the cap).
  const openRotaDialog = useCallback(() => {
    const weekdays = weekDays
      .filter((d) => d.day() >= 1 && d.day() <= 5)
      .slice(0, MAX_ROTA_DAYS)
      .map((d) => d.format("YYYY-MM-DD"));
    setRotaDays(weekdays);
    setRotaDialogOpen(true);
  }, [weekDays]);

  const toggleRotaDay = useCallback((dateStr) => {
    setRotaDays((prev) =>
      prev.includes(dateStr)
        ? prev.filter((d) => d !== dateStr)
        : prev.length >= MAX_ROTA_DAYS
        ? prev
        : [...prev, dateStr]
    );
  }, []);

  const handleGenerateRota = useCallback(async () => {
    const picked = rotaDays.slice().sort();
    if (!picked.length) {
      showToast?.("Pick at least one day.", "info");
      return;
    }
    setRotaBusy(true);
    try {
      const days = await Promise.all(
        picked.map(async (ds) => {
          const [dayGroups, dayLeaders] = await Promise.all([
            getActivityGroupsByDate(ds, selectedCampusName),
            getActivityLeaders(ds, selectedCampusName),
          ]);
          const forCampus = (dayGroups || []).filter(
            (g) => !g.campus || g.campus === selectedCampusName
          );
          const slotOf = (g) => String(g?.slot || "").toUpperCase();
          return {
            dateStr: ds,
            // Weekends use the DAY slot for the daytime programme; fold it into
            // the afternoon section so weekend picks aren't dropped.
            afternoon: forCampus.filter((g) =>
              ["AFTERNOON", "DAY"].includes(slotOf(g))
            ),
            evening: forCampus.filter((g) => slotOf(g) === "EVENING"),
            leaders: dayLeaders || [],
          };
        })
      );
      const hasAny = days.some((d) => d.afternoon.length || d.evening.length);
      if (!hasAny) {
        showToast?.("No activity groups on the selected days.", "info");
        return;
      }
      downloadWeeklyRotaPdf({
        campus: selectedCampusLabel || selectedCampusName,
        days,
        products,
      });
      setRotaDialogOpen(false);
    } catch (e) {
      console.error("Weekly rota PDF failed:", e);
      showToast?.("Could not build the rota PDF.", "error");
    } finally {
      setRotaBusy(false);
    }
  }, [rotaDays, selectedCampusName, selectedCampusLabel, products, showToast]);

  // Side columns can each collapse to a rail. Transport starts collapsed (and
  // so isn't fetched) until the user opens it.
  const [candidatesCollapsed, setCandidatesCollapsed] = useState(false);
  const [transportCollapsed, setTransportCollapsed] = useState(true);
  const showTransport = !transportCollapsed;

  // Transport components key off the campus id; the activities board tracks the
  // campus by name, so map back to the id from the campus list.
  const selectedCampusId = useMemo(() => {
    const match = campusList.find((c) => c.name === selectedCampusName);
    return match ? String(match.id) : "";
  }, [campusList, selectedCampusName]);

  const transport = useActivityTransport({
    selectedDate,
    selectedCampusId,
    campusList,
    showToast,
    // Always fetch journeys (not just when the transport column is open) so the
    // per-member bus/ticket icons on the group cards are populated regardless.
    enabled: true,
  });

  const handlePrevDay = () => {
    setSelectedDate(selectedDate.subtract(1, "day"));
  };

  const handleNextDay = () => {
    setSelectedDate(selectedDate.add(1, "day"));
  };

  const handleDateChange = (event) => {
    const newDate = dayjs(event.target.value);
    if (newDate.isValid()) {
      setSelectedDate(newDate);
    }
  };

  const handlePrintRegisters = () => {
    if (!filteredGroups.length) {
      showToast?.("No activity groups to print for this selection.", "info");
      return;
    }
    downloadActivityRegistersPdf({
      groups: filteredGroups,
      leaders,
      products,
      dateStr: campDateStr,
      campus: selectedCampusLabel || selectedCampusName,
      slot: selectedSlot,
    });
  };

  const handleSaveGroup = async ({ mode, id, payload }) => {
    try {
      await saveActivityGroup({ mode, id, payload });
      showToast?.(
        mode === "add" ? "Activity group created" : "Activity group updated",
        "success"
      );
      closeDialog();
      await refreshBoard();
    } catch (e) {
      console.error("Save activity group failed:", e);
      showToast?.("Save failed", "error");
      throw e;
    }
  };

  const handleDeleteGroup = async (group) => {
    try {
      await deleteActivityGroup(group.groupId);
      showToast?.("Activity group deleted", "success");
      await refreshBoard();
    } catch (e) {
      console.error("Delete activity group failed:", e);
      showToast?.("Delete failed", "error");
    }
  };

  // Pending "are you sure about this assignment?" confirmation — one dialog that
  // can carry several warning blocks (unbooked-for-free, and external/event
  // candidate going into a general activity).
  const [assignPrompt, setAssignPrompt] = useState(null);

  const memberKey = (m) => `${String(m?.personType).toUpperCase()}:${m?.personId}`;
  const nameOf = (m) => String(m?.name || "").trim() || `#${m?.personId}`;

  // The members list as loaded — sent as expectedMembersVersion so a save made
  // on top of someone else's change is refused rather than overwriting it.
  const membersVersionOf = (groupId) =>
    (groups || []).find((g) => String(g.groupId) === String(groupId))
      ?.membersVersion;

  const commitSaveMembers = async (groupId, nextMembers) => {
    try {
      await updateActivityGroupMembers(
        groupId,
        buildMemberPayload({
          members: nextMembers,
          startDate: campDateStr,
          endDate: campDateStr,
          expectedMembersVersion: membersVersionOf(groupId),
        })
      );
      showToast?.("Activity group updated", "success");
      await refreshBoard();
    } catch (e) {
      console.error("Save members failed:", e);
      if (e?.code === "STALE_MEMBERS") {
        // Someone else changed this group since it was loaded. Reload so the
        // card shows what's actually there, then explain — retrying on the
        // stale list would only replay the conflict.
        await refreshBoard();
        showToast?.(e.message, "warning");
        return;
      }
      showToast?.(e?.message || "Update failed", "error");
    }
  };

  const handleSaveMembers = async (groupId, nextMembers) => {
    const group = (groups || []).find(
      (g) => String(g.groupId) === String(groupId)
    );
    const product = (products || []).find(
      (p) => String(p.productId) === String(group?.productId)
    );
    const groupType = String(group?.productType || product?.productType || "").toUpperCase();

    const currentKeys = new Set(
      (Array.isArray(group?.members) ? group.members : []).map(memberKey)
    );
    // Only newly-added people are ever flagged — removals never warn.
    const added = (nextMembers || []).filter((m) => !currentKeys.has(memberKey(m)));

    // A person can now have SEVERAL candidate rows (one per booked product), so map
    // each person to the *set* of products they've booked — a single-row map would
    // collapse to the last row and mis-flag dual-booked students.
    const bookedIdsByPerson = new Map();
    (candidateRows || []).forEach((r) => {
      const k = `${String(r.personType).toUpperCase()}:${r.personId}`;
      if (!bookedIdsByPerson.has(k)) bookedIdsByPerson.set(k, new Set());
      if (r.bookedProductId != null) {
        bookedIdsByPerson.get(k).add(String(r.bookedProductId));
      }
    });
    const bookedIdsOf = (m) => bookedIdsByPerson.get(memberKey(m)) || new Set();

    // productId -> productType (needs `productType` on /api/activities/products;
    // until it lands, the external/event check below can't fire).
    const typeByProductId = new Map(
      (products || []).map((p) => [
        String(p.productId),
        String(p.productType || "").toUpperCase(),
      ])
    );

    const blocks = [];

    // (A) Adding people who didn't book a fee/approval-required product — they'd
    // join an activity others paid for, for free.
    if (product?.requiresApprovalForUnbookedAssignment && group?.productId != null) {
      const unbooked = added.filter(
        (m) => !bookedIdsOf(m).has(String(group.productId))
      );
      if (unbooked.length > 0) {
        blocks.push({
          message: `Students who booked “${
            product.productName || "this activity"
          }” may have paid a fee for it — these people would be added for free:`,
          people: unbooked.map(nameOf),
        });
      }
    }

    // (B) Event priority: a person booked onto an EVENT that day shouldn't be
    // quietly assigned to a non-event activity — their parents paid for the event,
    // and the receiving activity's leader would unwittingly try to take them along.
    // (Inert until `productType` is on /api/activities/products to identify events.)
    if (!isEventProductType(groupType)) {
      const onAnEvent = added.filter((m) =>
        Array.from(bookedIdsOf(m)).some((pid) =>
          isEventProductType(typeByProductId.get(String(pid)))
        )
      );
      if (onAnEvent.length > 0) {
        blocks.push({
          message:
            "These people are booked onto an event that day — adding them to a non-event activity would pull them off it:",
          people: onAnEvent.map(nameOf),
        });
      }
    }

    if (blocks.length > 0) {
      setAssignPrompt({ groupId, nextMembers, blocks });
      return;
    }

    await commitSaveMembers(groupId, nextMembers);
  };

  const handleConfirmAssign = async () => {
    const pending = assignPrompt;
    setAssignPrompt(null);
    if (pending) await commitSaveMembers(pending.groupId, pending.nextMembers);
  };

  // The campus's holding groups, found regardless of slot (one may have been
  // created while viewing a different slot). Used as the only assignment target
  // for the arrivers/leavers candidate cards.
  const arriversHoldingGroup = useMemo(
    () =>
      (groups || []).find(
        (g) =>
          String(g.campus) === String(selectedCampusName) &&
          holdingGroupKind(g) === "ARRIVERS"
      ) || null,
    [groups, selectedCampusName]
  );

  const departersHoldingGroup = useMemo(
    () =>
      (groups || []).find(
        (g) =>
          String(g.campus) === String(selectedCampusName) &&
          holdingGroupKind(g) === "DEPARTERS"
      ) || null,
    [groups, selectedCampusName]
  );

  const handleCreateHoldingGroup = async ({ kind, members, leaderStaffId }) => {
    if (!selectedCampusName || !campDateStr) return;

    // Never create a second holding group of the same kind for this campus/day —
    // route new arrivers/departers into the existing one instead.
    if (kind === "ARRIVERS" && arriversHoldingGroup) return;
    if (kind === "DEPARTERS" && departersHoldingGroup) return;

    const isArrivers = kind === "ARRIVERS";
    const label = isArrivers ? "Arrivers" : "Departers";
    const name = `${label} — ${selectedCampusName} — ${dayjs(campDateStr).format("ddd D MMM")}`;

    try {
      const created = await saveActivityGroup({
        mode: "add",
        payload: {
          name,
          campus: selectedCampusName,
          // Use the slot currently being viewed, not a hardcoded "DAY" — weekdays
          // have no DAY slot, so a DAY group would be created but never visible.
          slot: selectedSlot,
          dates: [campDateStr],
          capacity: 50,
          leaderStaffId: leaderStaffId ? Number(leaderStaffId) : null,
          productId: null,
          notes: null,
          activityKind: "PRODUCT",
          ratioChildrenPerAdult: 0,
        },
      });

      const newId = created?.groupId ?? created?.id;
      if (newId && Array.isArray(members) && members.length > 0) {
        await updateActivityGroupMembers(
          newId,
          buildMemberPayload({ members, startDate: campDateStr, endDate: campDateStr })
        );
      }

      showToast?.(`${label} group created`, "success");
      await refreshBoard();
    } catch (e) {
      console.error("Create holding group failed:", e);
      showToast?.(e?.message || "Create failed", "error");
    }
  };

  // Split a big WEEKEND group into one sub-group per staff member (plan built by
  // planWeekendSplit in the card). Several calls, not atomic — a mid-way failure
  // leaves a partial split; the user re-runs / fixes manually if so.
  const handleSplitGroup = async (plan) => {
    if (!plan || plan.groupId == null) return;
    const group = (groups || []).find(
      (g) => String(g.groupId) === String(plan.groupId)
    );
    if (!group) return;

    try {
      for (const sub of plan.subGroups) {
        const created = await saveActivityGroup({
          mode: "add",
          payload: {
            name: `${group.name} — ${sub.staffName}`,
            campus: group.campus || selectedCampusName,
            slot: group.slot || selectedSlot,
            dates:
              Array.isArray(group.dates) && group.dates.length
                ? group.dates
                : [campDateStr],
            capacity: group.capacity ?? 50,
            leaderStaffId: sub.staffId != null ? Number(sub.staffId) : null,
            productId: group.productId ?? null,
            notes: null,
            activityKind: group.activityKind || "PRODUCT",
            ratioChildrenPerAdult: group.ratioChildrenPerAdult ?? 0,
          },
        });
        const newId = created?.groupId ?? created?.id;
        if (newId && sub.students.length > 0) {
          await updateActivityGroupMembers(
            newId,
            buildMemberPayload({
              members: sub.students,
              startDate: campDateStr,
              endDate: campDateStr,
            })
          );
        }
      }

      // Trim the original group down to its leader's keep-list.
      await updateActivityGroupMembers(
        plan.groupId,
        buildMemberPayload({
          members: plan.originalMembers,
          startDate: campDateStr,
          endDate: campDateStr,
          expectedMembersVersion: group.membersVersion,
        })
      );

      showToast?.(`Split into ${plan.subGroups.length + 1} groups`, "success");
      await refreshBoard();
    } catch (e) {
      console.error("Split group failed:", e);
      // Sub-groups created before the failure stay; reload so they're visible.
      await refreshBoard();
      showToast?.(
        e?.message || "Split failed",
        e?.code === "STALE_MEMBERS" ? "warning" : "error"
      );
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: "wrap" }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={handlePrevDay}>
            <NavigateBefore />
          </IconButton>

          <TextField
            type="date"
            size="small"
            value={campDateStr || ""}
            onChange={handleDateChange}
          />

          <IconButton onClick={handleNextDay}>
            <NavigateNext />
          </IconButton>
        </Stack>

        <FormControl size="small" sx={{ minWidth: 220 }} disabled={campusesLoading}>
          <InputLabel>Campus</InputLabel>
          <Select
            label="Campus"
            value={selectedCampusName ?? ""}
            onChange={(e) => setSelectedCampusName(e.target.value)}
          >
            <MenuItem value="">
              <em>Select campus…</em>
            </MenuItem>
            {campusList.map((c) => (
              <MenuItem key={c.id} value={c.name}>
                {c.name}
              </MenuItem>
            ))}
            <MenuItem value="UNASSIGNED">Unassigned</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Slot</InputLabel>
          <Select
            label="Slot"
            value={selectedSlot}
            onChange={(e) => setSelectedSlot(e.target.value)}
          >
            {slotOptions.map((slotOption) => (
              <MenuItem key={slotOption.value} value={slotOption.value}>
                {slotOption.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={refreshBoard}
          disabled={loading || referenceDataLoading}
        >
          Refresh
        </Button>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() =>
            openAddDialog({
              slot: selectedSlot,
              date: campDateStr,
              campus: selectedCampusName || "",
            })
          }
          disabled={loading || referenceDataLoading || !campDateStr}
        >
          Add activity group
        </Button>

        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={() => setCopyDialogOpen(true)}
          disabled={!campDateStr}
        >
          Copy Activity Groups
        </Button>

        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrintRegisters}
          disabled={!campDateStr || !selectedCampusName || filteredGroups.length === 0}
        >
          Print registers
        </Button>

        <Button
          variant="outlined"
          startIcon={<EventNoteIcon />}
          onClick={openRotaDialog}
          disabled={!hasConcreteCampus}
          title={
            hasConcreteCampus
              ? undefined
              : "Choose a specific campus to build its weekly rota"
          }
        >
          Activity Rota
        </Button>

        {loading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2">Loading…</Typography>
          </Stack>
        )}

        {error && (
          <Typography variant="body2" color="error">
            {String(error?.message || error)}
          </Typography>
        )}

        {referenceDataError && (
          <Typography variant="body2" color="error">
            {String(referenceDataError?.message || referenceDataError)}
          </Typography>
        )}
      </Stack>

      <AddEditActivityDialog
        open={dialogOpen}
        onClose={closeDialog}
        campDate={campDateStr}
        slot={selectedSlot}
        campuses={campusList}
        leaders={leaders}
        products={products}
        group={editingGroup}
        existingGroups={groups}
        onSave={handleSaveGroup}
        disabled={loading || referenceDataLoading}
      />

      <CopyGroupsDialog
        open={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        targetDate={campDateStr}
        onCopied={refreshBoard}
      />

      <Dialog
        open={rotaDialogOpen}
        onClose={() => (rotaBusy ? null : setRotaDialogOpen(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Activity Rota PDF</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {selectedCampusLabel || selectedCampusName} — pick the days to
            include (up to {MAX_ROTA_DAYS}, to fit a portrait page). Afternoon
            and evening groups only.
          </Typography>
          <FormGroup>
            {weekDays.map((d) => {
              const ds = d.format("YYYY-MM-DD");
              const checked = rotaDays.includes(ds);
              const atCap = rotaDays.length >= MAX_ROTA_DAYS;
              return (
                <FormControlLabel
                  key={ds}
                  control={
                    <Checkbox
                      checked={checked}
                      disabled={rotaBusy || (!checked && atCap)}
                      onChange={() => toggleRotaDay(ds)}
                    />
                  }
                  label={d.format("dddd D MMM")}
                />
              );
            })}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotaDialogOpen(false)} disabled={rotaBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateRota}
            disabled={rotaBusy || rotaDays.length === 0}
            startIcon={rotaBusy ? <CircularProgress size={16} /> : <EventNoteIcon />}
          >
            {rotaBusy ? "Building…" : "Generate PDF"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(assignPrompt)}
        onClose={() => setAssignPrompt(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add these people?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {(assignPrompt?.blocks || []).map((block, bi) => (
              <Box key={bi}>
                <Typography variant="body2">{block.message}</Typography>
                <List dense sx={{ mt: 0.5 }}>
                  {(block.people || []).map((name, i) => (
                    <ListItem key={`${name}-${i}`} disableGutters sx={{ py: 0 }}>
                      <ListItemText primary={name} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignPrompt(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmAssign}>
            Add anyway
          </Button>
        </DialogActions>
      </Dialog>

      {campusesLoading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading campuses…</Typography>
        </Stack>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "stretch" }}>
          <CollapsibleColumn
            side="left"
            label="Candidates"
            collapsed={candidatesCollapsed}
            onToggle={() => setCandidatesCollapsed((v) => !v)}
          >
            <ActivityCandidatesPanel
              selectedCampusName={selectedCampusName}
              selectedCampusLabel={selectedCampusLabel}
              selectedSlot={selectedSlot}
              unbookedCandidates={unbookedCandidates}
              bookedCandidateGroups={bookedCandidateGroups}
              candidatesLoading={candidatesLoading}
              candidatesError={candidatesError}
              activityGroups={filteredGroups}
              onAssignMembers={handleSaveMembers}
              arriversHoldingGroup={arriversHoldingGroup}
              departersHoldingGroup={departersHoldingGroup}
              leaders={leaders}
              onCreateHoldingGroup={handleCreateHoldingGroup}
            />
          </CollapsibleColumn>

          <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
            <ActivityGroupsPanel
              selectedCampusName={selectedCampusName}
              selectedCampusLabel={selectedCampusLabel}
              selectedSlot={selectedSlot}
              groups={filteredGroups}
              leaders={leaders}
              products={products}
              onEditGroup={openEditDialog}
              onDeleteGroup={handleDeleteGroup}
              onSaveMembers={handleSaveMembers}
              journeys={transport.journeys}
              onAssignMembersToBus={
                showTransport && can("transport")
                  ? transport.assignMembersToBus
                  : undefined
              }
              onSplitGroup={handleSplitGroup}
            />
          </Box>

          <CollapsibleColumn
            side="right"
            label="Transport"
            collapsed={transportCollapsed}
            onToggle={() => setTransportCollapsed((v) => !v)}
          >
            <ActivityTransportColumn
              selectedCampusId={selectedCampusId}
              campusList={campusList}
              transport={transport}
            />
          </CollapsibleColumn>
        </Box>
      )}
    </Box>
  );
}